import {
  deleteToken,
  getMessaging,
  getToken,
  onMessage,
} from "firebase/messaging";
import axios from "axios";

import logger from 'utils/logger';
// Chave VAPID: obter em Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
const VAPID_KEY = import.meta.env.VITE_FCM_VAPID_KEY;

const FCM_TOKEN_KEY = "fcm_token";
let _messagingInstance = null;

/**
 * Inicializa o Firebase Messaging com a instância do app Firebase.
 * Deve ser chamado após initializeApp().
 */
export function initMessaging(firebaseApp) {
  try {
    // Verificar suporte do browser
    if (!("serviceWorker" in navigator) || !("Notification" in window)) {
      logger.warn("[FCM] Browser não suporta notificações push.");
      return null;
    }
    _messagingInstance = getMessaging(firebaseApp);
    return _messagingInstance;
  } catch (error) {
    logger.error("[FCM] Erro ao inicializar messaging:", error);
    return null;
  }
}

/**
 * Solicita permissão de notificação e registra o token FCM no backend.
 * Retorna o token ou null se negado/erro.
 */
export async function requestNotificationPermission(firebaseApp) {
  if (!("Notification" in window)) {
    logger.warn("[FCM] API de Notificação não disponível.");
    return null;
  }

  const permission = await Notification.requestPermission();

  if (permission !== "granted") {
    logger.info("[FCM] Permissão de notificação negada pelo usuário.");
    return null;
  }

  if (!_messagingInstance && firebaseApp) {
    initMessaging(firebaseApp);
  }

  if (!_messagingInstance) {
    logger.warn("[FCM] Messaging não inicializado.");
    return null;
  }

  try {
    if (!VAPID_KEY) {
      logger.error(
        "[FCM] VITE_FCM_VAPID_KEY não configurado. Acesse Firebase Console > Project Settings > Cloud Messaging > Web Push certificates para obter a chave.",
      );
      return null;
    }

    const token = await getToken(_messagingInstance, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: await navigator.serviceWorker.ready,
    });

    if (token) {
      logger.info("[FCM] Token FCM obtido com sucesso.");
      await _saveFCMTokenToBackend(token);
      return token;
    }

    logger.warn("[FCM] Nenhum token FCM retornado.");
    return null;
  } catch (error) {
    logger.error("[FCM] Erro ao obter token FCM:", error);
    return null;
  }
}

/**
 * Salva o token FCM no backend e no localStorage.
 */
async function _saveFCMTokenToBackend(token) {
  try {
    // Evitar re-salvar o mesmo token
    if (localStorage.getItem(FCM_TOKEN_KEY) === token) return;

    await axios.post("/v1/notifications/token", { token });
    localStorage.setItem(FCM_TOKEN_KEY, token);
    logger.info("[FCM] Token registrado no backend.");
  } catch (error) {
    logger.error("[FCM] Erro ao registrar token no backend:", error);
  }
}

/**
 * Remove o token FCM do backend e do localStorage.
 * Chamar no logout do usuário.
 */
export async function removeFCMToken() {
  try {
    const token = localStorage.getItem(FCM_TOKEN_KEY);
    if (!token) return;

    // Invalidar token no Firebase
    if (_messagingInstance) {
      await deleteToken(_messagingInstance).catch(() => {});
    }

    // Remover do backend
    await axios.delete("/v1/notifications/token", { data: { token } });
    localStorage.removeItem(FCM_TOKEN_KEY);
    logger.info("[FCM] Token FCM removido.");
  } catch (error) {
    logger.error("[FCM] Erro ao remover token FCM:", error);
  }
}

/**
 * Registra callback para mensagens recebidas em FOREGROUND (app aberto).
 * Retorna a função de unsubscribe.
 */
export function onForegroundMessage(callback) {
  if (!_messagingInstance) return () => {};
  return onMessage(_messagingInstance, callback);
}

/**
 * Retorna a instância atual do Firebase Messaging.
 */
export function getMessagingInstance() {
  return _messagingInstance;
}
